import React, { useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';

interface TaxonomyCategory {
  id: string;
  labelKey: string;
  options: { value: string; labelKey: string }[];
}

export interface TaxonomyValue {
  category: string;
  subItem: string;
  note: string;
}

interface TaxonomyQuestionProps {
  questionNumber: number;
  labelKey: string;
  hintKey?: string;
  notePlaceholderKey?: string;
  categories: TaxonomyCategory[];
  value: TaxonomyValue | null;
  onChange: (value: TaxonomyValue | null) => void;
}

export function TaxonomyQuestion({
  questionNumber,
  labelKey,
  hintKey,
  notePlaceholderKey,
  categories,
  value,
  onChange,
}: TaxonomyQuestionProps) {
  const { t } = useTranslation();
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategory((prev) => (prev === categoryId ? null : categoryId));
  };

  const selectOption = (categoryId: string, subItem: string) => {
    onChange({
      category: categoryId,
      subItem,
      note: value?.note ?? '',
    });
  };

  const clearTag = () => {
    onChange(value ? { category: '', subItem: '', note: value.note } : null);
    // Note: clearing the tag preserves the note (user might keep typing).
    // If the note is empty too, the form normaliser drops the whole answer.
  };

  const updateNote = (note: string) => {
    if (value && (value.category || value.subItem)) {
      onChange({ ...value, note });
    } else {
      onChange({ category: '', subItem: '', note });
    }
  };

  const selectedCategory = value?.category
    ? categories.find((c) => c.id === value.category)
    : undefined;
  const selectedOption = selectedCategory?.options.find((o) => o.value === value?.subItem);
  const hasTag = Boolean(selectedCategory && selectedOption);

  return (
    <div className="bg-white rounded-xl p-4 mb-3 shadow-sm">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-amber-500 text-white text-xs font-medium shrink-0">
          {questionNumber}
        </span>
        <span className="text-[15px] font-semibold text-gray-800">{t(labelKey)}</span>
      </div>
      {hintKey && (
        <p className="text-xs text-gray-500 italic mb-3 ml-[30px]">{t(hintKey)}</p>
      )}

      {categories.map((cat) => {
        const isExpanded = expandedCategory === cat.id;
        const hasSelected = value?.category === cat.id;
        return (
          <div
            key={cat.id}
            className={`rounded-[10px] mb-1.5 overflow-hidden border-[1.5px] transition-all ${
              hasSelected ? 'bg-amber-100 border-amber-500' : 'bg-stone-100 border-gray-200'
            }`}
          >
            <button
              type="button"
              onClick={() => toggleCategory(cat.id)}
              aria-expanded={isExpanded}
              className="w-full flex items-center justify-between px-3.5 py-3 text-sm font-medium text-gray-800 select-none"
            >
              <span>{t(cat.labelKey)}</span>
              <ChevronRight
                size={18}
                className={`text-gray-400 transition-transform ${
                  isExpanded ? 'rotate-90' : ''
                }`}
              />
            </button>
            {isExpanded && (
              <div className="px-3.5 pb-3 pt-2.5 border-t border-gray-200 bg-white flex flex-col gap-1">
                {cat.options.map((opt) => {
                  const isSelected =
                    value?.category === cat.id && value?.subItem === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => selectOption(cat.id, opt.value)}
                      className={`text-left px-3 py-2.5 rounded-lg text-sm border-[1.5px] transition-all ${
                        isSelected
                          ? 'bg-amber-100 text-amber-700 font-semibold border-amber-500'
                          : 'text-gray-500 border-transparent hover:bg-stone-100'
                      }`}
                    >
                      {t(opt.labelKey)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {hasTag && selectedCategory && selectedOption && (
        <div className="inline-flex items-center gap-2 bg-amber-500 text-white rounded-lg px-3 py-2 mt-3 mb-2 text-[13px] font-medium">
          <span className="opacity-85 text-xs">{t(selectedCategory.labelKey)}</span>
          <span className="opacity-60">·</span>
          <span className="font-semibold">{t(selectedOption.labelKey)}</span>
          <button
            type="button"
            onClick={clearTag}
            aria-label="Clear"
            className="bg-white/25 hover:bg-white/40 rounded-full w-[22px] h-[22px] flex items-center justify-center ml-1"
          >
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>
      )}

      <textarea
        value={value?.note ?? ''}
        onChange={(e) => updateNote(e.target.value)}
        placeholder={notePlaceholderKey ? t(notePlaceholderKey) : ''}
        className="w-full mt-2 border-[1.5px] border-gray-200 rounded-[10px] p-3 text-sm bg-white text-gray-800 resize-y min-h-[80px] focus:outline-none focus:border-amber-500 placeholder:text-gray-400"
      />
    </div>
  );
}
